import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { CommitmentDetail } from '@contracts/ledger';
import {
  useCommitment,
  useLedgerActions,
  useMe,
  useNames,
  useUpdateCommitment,
} from '../api/queries';
import {
  BackLink,
  Broken,
  Button,
  Card,
  Empty,
  MenuButton,
  Pill,
  SkeletonRows,
  Who,
  useNotices,
} from '../ui';
import { between, dayLabel, duration, since } from '../lib/time';
import { AskBox } from './Answer';
import { DueButton } from '../ledger/DueButton';
import { Translated } from '../meeting/Translated';

/**
 * A commitment is a page, not a line — because a dry line of text is not a
 * memory (Vladimir, 2026-09-18). What was said, with the lines around it;
 * the meeting it came from and what else that meeting produced; the chain of
 * hand-overs; what the same person still owes in the series; where it stands.
 * The four ends and the wording are edited here, by editors of the meeting.
 */
export function CommitmentPage(): ReactElement {
  const { id = '' } = useParams();
  // "Show original" (PRD 57), for this action only.
  const [originalOf, setOriginalOf] = useState<string | null>(null);
  const original = originalOf === id;
  const q = useCommitment(id, { original });
  return (
    <div className="page stack-lg">
      {q.isPending ? (
        <SkeletonRows rows={6} />
      ) : q.isError ? (
        <Card flush>
          {q.error.message.includes('404') ? (
            <Empty tone="none" lead="No such commitment — or not one you can open.">
              A commitment is readable by whoever can open its meeting.
            </Empty>
          ) : (
            <Broken what="The commitment" retry={() => void q.refetch()} />
          )}
        </Card>
      ) : (
        <Loaded d={q.data} onToggleOriginal={() => setOriginalOf(original ? null : id)} />
      )}
    </div>
  );
}

function Loaded({
  d,
  onToggleOriginal,
}: {
  d: CommitmentDetail;
  onToggleOriginal: () => void;
}): ReactElement {
  const me = useMe();
  const names = useNames();
  const ledger = useLedgerActions();
  const update = useUpdateCommitment();
  const { show } = useNotices();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(d.item.text);
  const item = d.item;
  const open = item.status === 'OPEN';
  const mine = item.direction === 'I_OWE';
  const myEmail = me.data?.email ?? '';
  const nameOf = (email: string | null, fallback: string | null) =>
    (email ? names.get(email.toLowerCase()) : null) ?? fallback ?? null;

  const end = (status: 'DONE' | 'DROPPED') =>
    ledger.close.mutate(
      { id: item.id, body: { status } },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            text:
              status === 'DONE'
                ? 'Marked done.'
                : 'Dropped — it stays readable and asks for no reason.',
            action: { label: 'Undo', onSelect: () => ledger.reopen.mutate(item.id) },
          }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  const reopen = () =>
    ledger.reopen.mutate(item.id, {
      onSuccess: () => show({ tone: 'success', text: 'Open again.' }),
      onError: (err) => show({ tone: 'failure', text: err.message }),
    });
  const giveTo = (email: string, name: string | null) =>
    ledger.assign.mutate(
      { id: item.id, body: { email, ...(name ? { name } : {}) } },
      {
        onSuccess: () => show({ tone: 'success', text: `${name ?? email} owns it now.` }),
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  // ⚠ From the words on screen — the translation, or the original — so what
  // is saved is what the person was looking at. An edit in either language
  // becomes the source, and the others are translated from it (PRD 57).
  const startEditing = () => {
    setDraft(item.text);
    setEditing(true);
  };
  const save = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (text.length < 3 || text === item.text) return setEditing(false);
    update.mutate(
      { id: item.id, body: { text } },
      {
        onSuccess: () => {
          setEditing(false);
          show({ tone: 'success', text: 'Reworded — and confirmed, since a person wrote it.' });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };

  const candidates = [
    ...(myEmail ? [{ email: myEmail, name: me.data?.name ?? null }] : []),
    ...d.meeting.attendees.filter((a) => a.email.toLowerCase() !== myEmail.toLowerCase()),
  ];
  const span = d.lines.filter((l) => l.inSpan);
  const said = span[0]?.spokeAt ?? null;
  const seconds =
    item.span.startMs != null && item.span.endMs != null
      ? Math.max(1, Math.round((item.span.endMs - item.span.startMs) / 1000))
      : null;

  return (
    <>
      <div className="toolbar">
        <BackLink to="/open">Open</BackLink>
        <span className="crumb">
          Commitment · <Link to={`/m/${d.meeting.id}`}>{d.meeting.title}</Link> ·{' '}
          {dayLabel(d.meeting.startedAt)}
        </span>
        {d.access.canEdit ? (
          <div className="actions">
            {open ? (
              <>
                <MenuButton
                  label={
                    item.ownerEmail
                      ? `Owner: ${nameOf(item.ownerEmail, item.ownerName) ?? item.ownerEmail}`
                      : 'Give an owner'
                  }
                  items={candidates.map((c) => ({
                    label: c.name ?? c.email,
                    sub: c.email,
                    current: item.ownerEmail?.toLowerCase() === c.email.toLowerCase(),
                    onSelect: () => giveTo(c.email, c.name),
                  }))}
                />
                <DueButton id={item.id} dueAt={item.dueAt} size="md" />
                <Button
                  variant="primary"
                  onClick={() => end('DONE')}
                  working={ledger.close.isPending ? '…' : false}
                >
                  Mark done
                </Button>
                <MenuButton
                  label="More"
                  items={[
                    { label: 'Edit the wording', onSelect: () => startEditing() },
                    { label: 'Drop it', danger: true, onSelect: () => end('DROPPED') },
                  ]}
                />
              </>
            ) : (
              <>
                {/* Where it went, one press away — the task is where it lives now. */}
                {item.status === 'EXPORTED' && item.asanaTaskUrl ? (
                  <a
                    className="btn btn-primary"
                    href={item.asanaTaskUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Asana ↗
                  </a>
                ) : null}
                <Button onClick={reopen} working={ledger.reopen.isPending ? '…' : false}>
                  Reopen
                </Button>
              </>
            )}
          </div>
        ) : null}
      </div>

      <div className="stack" style={{ gap: 6 }}>
        {editing ? (
          <form onSubmit={save} className="stack" style={{ gap: 8 }}>
            <textarea
              className="field"
              aria-label="The commitment"
              value={draft}
              rows={2}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
                }
                if (e.key === 'Escape') setEditing(false);
              }}
              style={{ font: 'var(--t-title)', width: '100%', maxWidth: '40ch' }}
            />
            <div className="cluster" style={{ gap: 8 }}>
              <Button
                type="submit"
                variant="primary"
                working={update.isPending ? 'Saving…' : false}
              >
                Save
              </Button>
              <Button variant="quiet" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <h1 className="t-title" style={{ textWrap: 'balance', maxWidth: '40ch' }}>
            {item.text}{' '}
            {d.access.canEdit && open ? (
              <button type="button" className="btn btn-quiet btn-sm" onClick={startEditing}>
                Edit
              </button>
            ) : null}
          </h1>
        )}
        <Translated state={d.translation} onToggle={onToggleOriginal} />
        <p className="mono cluster" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {item.ownerEmail ? (
            <Who
              email={item.ownerEmail}
              name={nameOf(item.ownerEmail, item.ownerName)}
              me={mine}
              to={`/people/${encodeURIComponent(item.ownerEmail)}`}
            />
          ) : (
            <Who email="" name={null} />
          )}
          <Pill
            tone={
              item.grade === 'CONFIRMED' ? 'green' : item.grade === 'ASSUMED' ? 'amber' : undefined
            }
          >
            {item.grade.toLowerCase()}
          </Pill>
          <Pill
            tone={
              open
                ? undefined
                : item.status === 'DONE' || item.status === 'EXPORTED'
                  ? 'green'
                  : undefined
            }
          >
            {open
              ? 'open'
              : item.status === 'EXPORTED'
                ? 'sent to Asana'
                : item.status.toLowerCase().replace('_', ' ')}
          </Pill>
          <span>
            {said ? `said at ${clockOf(said)}` : 'said'} in {d.meeting.title}
            {d.meeting.seriesTitle && d.meeting.seriesTitle !== d.meeting.title
              ? ` · ${d.meeting.seriesTitle}`
              : ''}
          </span>
          <span>· {since(item.createdAt)}</span>
          {item.handedOverFrom ? (
            <Pill>from {item.handedOverFrom.name ?? item.handedOverFrom.email}</Pill>
          ) : null}
          {item.postedToSlack ? <Pill tone="green">in Slack</Pill> : null}
        </p>
      </div>

      <div className="grid-2 aside">
        <div className="stack-lg">
          <Card
            title="What was said"
            aside={
              d.canReadTranscript && item.span.startSegmentId ? (
                <Link
                  to={`/m/${d.meeting.id}#line-${item.span.startSegmentId}`}
                  className="t-secondary"
                >
                  {d.meeting.video && seconds
                    ? `Play these ${seconds} seconds →`
                    : 'Open the moment →'}
                </Link>
              ) : null
            }
            flush
          >
            {!d.canReadTranscript ? (
              <p className="t-secondary" style={{ padding: '14px 16px' }}>
                The transcript is not yours to read; the note and the ledger are.
              </p>
            ) : d.lines.length === 0 ? (
              <p className="t-secondary" style={{ padding: '14px 16px' }}>
                No lines are marked for this one — it was typed, or came from an earlier version of
                the extraction.
              </p>
            ) : (
              d.lines.map((l) => (
                <div key={l.id} className={`cite ${l.inSpan ? 'on' : 'dim'}`}>
                  <span className="when">{l.spokeAt ? clockOf(l.spokeAt, true) : ''}</span>
                  <span>
                    <span className={l.inSpan ? 'who-strong' : 't-secondary'}>
                      {l.speakerName ?? 'Unknown'}:
                    </span>{' '}
                    {l.inSpan ? l.text : <span className="t-secondary">{l.text}</span>}
                  </span>
                </div>
              ))
            )}
          </Card>

          <Card
            title="The meeting"
            aside={
              <Link to={`/m/${d.meeting.id}`} className="t-secondary">
                Open the meeting →
              </Link>
            }
          >
            <div className="stack" style={{ gap: 10 }}>
              <p className="mono">
                {dayLabel(d.meeting.startedAt)} · {clockOf(d.meeting.startedAt)}
                {d.meeting.endedAt
                  ? `–${clockOf(d.meeting.endedAt)} · ${duration(between(d.meeting.startedAt, d.meeting.endedAt))}`
                  : ''}
                {d.meeting.seriesKey ? (
                  <>
                    {' · '}
                    <Link to={`/series/${encodeURIComponent(d.meeting.seriesKey)}`}>
                      {d.meeting.seriesTitle ?? d.meeting.seriesKey}
                    </Link>
                  </>
                ) : null}
              </p>
              {d.meeting.summary.length ? (
                d.meeting.summary.map((p, i) => (
                  <p key={i} className="t-body" style={{ maxWidth: '65ch' }}>
                    {p}
                  </p>
                ))
              ) : (
                <p className="t-secondary">No note yet.</p>
              )}
              <div className="cluster" style={{ gap: 6, flexWrap: 'wrap' }}>
                {d.meeting.attendees.map((a) => (
                  <Who
                    key={a.email}
                    email={a.email}
                    name={nameOf(a.email, a.name)}
                    me={a.email.toLowerCase() === myEmail.toLowerCase()}
                    to={`/people/${encodeURIComponent(a.email)}`}
                    short
                  />
                ))}
              </div>
            </div>
          </Card>

          {d.siblings.commitments.length + d.siblings.decisions.length > 0 ? (
            <Card title="Also from this meeting" flush>
              <div className="rows">
                {d.siblings.commitments.map((c) => (
                  <Link key={c.id} to={`/c/${c.id}`} className="row">
                    <span className="row-main">
                      <span className="row-title">{c.text}</span>
                      <span className="row-sub">
                        <span className="mono">{c.ownerName ?? c.ownerEmail ?? 'nobody yet'}</span>
                        <Pill>{c.status.toLowerCase().replace('_', ' ')}</Pill>
                      </span>
                    </span>
                  </Link>
                ))}
                {d.siblings.decisions.map((x) => (
                  <div key={x.id} className="row">
                    <span className="row-main">
                      <span className="row-title">{x.text}</span>
                      <span className="row-sub">
                        <Pill tone={x.status === 'STANDS' ? 'green' : undefined}>
                          {x.status === 'STANDS'
                            ? 'decision · stands'
                            : `decision · ${x.status.toLowerCase()}`}
                        </Pill>
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>

        <div className="stack-lg">
          <Card title="Where it stands" flush>
            {d.history.map((h, i) => (
              <div key={i} className="cite">
                <span className="when">
                  {dayLabel(h.at)} · {clockOf(h.at)}
                </span>
                <span>
                  {h.what}
                  {h.who ? ` by ${h.who.name ?? h.who.email}` : ''}
                  {h.meeting ? (
                    <>
                      {' '}
                      in <Link to={`/m/${h.meeting.id}`}>{h.meeting.title}</Link>
                    </>
                  ) : null}
                </span>
              </div>
            ))}
          </Card>

          {d.chain.length > 1 ? (
            <Card
              title="The chain"
              aside={<span className="t-secondary">hand-overs, oldest first</span>}
              flush
            >
              {d.chain.map((c) => (
                <div key={c.id} className={`cite ${c.current ? 'on' : ''}`}>
                  <span className="when">
                    {dayLabel(c.createdAt)} · {c.status.toLowerCase().replace('_', ' ')}
                  </span>
                  <span>
                    {c.ownerName ?? c.ownerEmail ?? 'nobody'}
                    {c.current ? '' : ' → '}
                    {c.current ? null : (
                      <Link to={`/c/${c.id}`} className="t-secondary">
                        open this version
                      </Link>
                    )}
                  </span>
                </div>
              ))}
            </Card>
          ) : null}

          {d.related.length > 0 ? (
            <Card
              title={`Still open in ${d.meeting.seriesTitle ?? 'this series'}`}
              aside={
                <span className="t-secondary">
                  for {nameOf(item.ownerEmail, item.ownerName) ?? 'the same owner'}
                </span>
              }
              flush
            >
              <div className="rows">
                {d.related.map((r) => (
                  <Link key={r.id} to={`/c/${r.id}`} className="row">
                    <span className="row-main">
                      <span className="row-title">{r.text}</span>
                      <span className="row-sub">
                        <span className="mono">
                          {r.meeting.title} · {since(r.createdAt)}
                        </span>
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </Card>
          ) : null}

          <Card title="Ask about this">
            <div className="stack" style={{ gap: 10 }}>
              <div className="cluster" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Link
                  to={`/m/${d.meeting.id}/answer?q=${encodeURIComponent(`What was said about “${item.text}”, and what exactly was agreed?`)}`}
                  className="chip"
                >
                  What exactly was agreed?
                </Link>
                <Link
                  to={`/m/${d.meeting.id}/answer?q=${encodeURIComponent(`Why does “${item.text}” matter, and what depends on it?`)}`}
                  className="chip"
                >
                  Why does it matter?
                </Link>
              </div>
              <AskBox
                scope="meeting"
                scopeId={d.meeting.id}
                placeholder="Ask about this commitment…"
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function clockOf(iso: string, seconds = false): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    hour12: false,
  });
}
